// INT-12 — re-open the Monaco suggest widget on the cold→warm schema
// transition.
//
// Problem: when the user triggers completion before the schema snapshot has
// been published (cold cache), the resolver can only return keyword-level
// suggestions. The fire-and-forget schema warm publishes shortly after via
// `schemaBus.publish` → an `'updated'` event. Without this helper the stale
// keyword-only list stays on screen until the user types again.
//
// Fix: subscribe to the bus for the editor's connection and, on the FIRST
// `'updated'` event for the editor's `(connectionId, database)` while the
// editor has focus AND a suggest list is on screen, programmatically re-run
// suggest so the now-warm schema-aware candidates replace the cold list. We
// fire ONLY on the cold→warm transition, never on later refreshes (a DDL
// invalidation + re-publish must not yank the widget open while the user is
// mid-type).
//
// The "list is on screen" gate is what makes this a re-trigger and not an
// opener (2026-09-21): a query tab focuses its editor on mount while the
// language service warms the schema in the background, so with focus as the
// only gate the warm's first publish (phase 1 of the tiered cache, or a
// later throttled phase-2 page) opened the widget in an untouched editor —
// "the tab opens with autocomplete already open". Same after a reconnect, a
// database switch or a manual refresh. Focus alone is not a request for
// completion; an open widget is.
//
// The re-run itself goes through `rerunSuggestSession` (`suggestWidget.ts`),
// not `editor.action.triggerSuggest`: that action is disabled while the list
// is visible (its precondition), so it could only ever OPEN a closed list —
// the popup — and never refresh an open one.
//
// All decision logic lives here (pure-ish, fakeable) so `SqlMonaco.tsx`
// stays a one-line wiring shell. Unit-tested in
// `retriggerSuggestOnWarm.test.ts`.

import type { SchemaBusEvent, subscribe as SubscribeFn } from './schemaBus';

/** Minimal Monaco editor surface this helper needs (fakeable in tests). */
export type WarmSuggestEditorHandle = {
  hasTextFocus(): boolean;
};

export type WarmSuggestRetriggerDeps = {
  readonly subscribe: typeof SubscribeFn;
  readonly connectionId: string;
  readonly database: string | undefined;
  /**
   * Resolves the live editor handle at event time (the editor ref may still
   * be null when the subscription is wired). Returns `null` if unavailable.
   */
  readonly getEditorHandle: () => WarmSuggestEditorHandle | null;
  /**
   * Is a suggest list currently showing in THIS editor? Read at event time.
   * Only an open list can be stale, so only an open list is re-run — a
   * focused editor with no list open gets nothing (see the header).
   */
  readonly isSuggestWidgetOpen: () => boolean;
  /** Re-runs the open suggest session in place (`rerunSuggestSession`). */
  readonly rerunSuggest: () => void;
  /**
   * Optional "suggest while typing" gate, read at event time. When it returns
   * true the cold→warm transition is still latched but the widget is NOT
   * opened — the user turned automatic suggestions off, and an unsolicited
   * popup would be exactly what they asked not to see.
   */
  readonly isSuppressed?: () => boolean;
};

/**
 * Subscribes to the schema bus and returns an unsubscribe cleanup. On the
 * first matching `'updated'` event (cold→warm) with the editor focused and a
 * suggest list already open, it calls `deps.rerunSuggest()`.
 */
export function makeWarmSuggestRetrigger(
  deps: WarmSuggestRetriggerDeps,
): () => void {
  let warmed = false;

  const onEvent = (event: SchemaBusEvent): void => {
    if (warmed) return;
    if (event.type !== 'updated') return;
    if (event.connectionId !== deps.connectionId) return;
    if (event.database !== deps.database) return;

    const handle = deps.getEditorHandle();
    if (!handle) return;
    // Latch the transition even if the editor is unfocused: a "warm" event we
    // observed is no longer cold. We only *act* (open suggest) when focused,
    // but we must not re-arm for a later refresh of the same connection.
    warmed = true;
    if (deps.isSuppressed?.()) return;
    if (!handle.hasTextFocus()) return;
    // Nothing on screen → nothing stale to replace. Opening a list here would
    // be the unsolicited popup this module must never produce.
    if (!deps.isSuggestWidgetOpen()) return;
    deps.rerunSuggest();
  };

  return deps.subscribe(deps.connectionId, onEvent);
}
